import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getLocale } from '../services/CommonServices';

// 로컬 기본 번역 파일 import
import koMessages from '../locale/ko.json';
import enMessages from '../locale/en.json';

const LanguageContext = createContext();

// 기본 메시지 (로컬 파일 기반)
const defaultMessages = {
  ko: koMessages,
  en: enMessages,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, memberId }) => {
  const [locale, setLocale] = useState(() => localStorage.getItem('lang') || 'ko');
  const [messages, setMessages] = useState(defaultMessages['ko']);
  const [allMessages, setAllMessages] = useState(defaultMessages);
  const [availableLangs, setAvailableLangs] = useState(['ko', 'en']);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 먼저 로컬 메시지로 초기화
    setMessages(defaultMessages[locale] || defaultMessages['ko']);

    // memberId가 없으면 로컬 메시지만 사용
    if (!memberId) {
      setIsLoading(false);
      return;
    }

    // API에서 추가/커스텀 메시지 가져오기
    getLocale(memberId)
      .then((result) => {
        if (result.data.success && result.data.messages) {
          // API 메시지를 로컬 메시지와 병합 (API가 우선)
          const mergedMessages = {
            ko: { ...defaultMessages.ko, ...result.data.messages.ko },
            en: { ...defaultMessages.en, ...result.data.messages.en },
          };

          if (result.data.langs) {
            setAvailableLangs(result.data.langs);
          }
          setAllMessages(mergedMessages);
          setMessages(mergedMessages[locale] || mergedMessages['ko']);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch locale from API, using local messages:', err);
        // API 실패 시에도 로컬 메시지 사용
        setIsLoading(false);
      });
  }, [memberId]);

  useEffect(() => {
    if (Object.keys(allMessages).length > 0) {
      setMessages(allMessages[locale] || allMessages['ko']);
    }
  }, [locale, allMessages]);

  const changeLanguage = useCallback((newLocale) => {
    if (newLocale === locale) return;

    setLocale(newLocale);
    localStorage.setItem('lang', newLocale);
  }, [locale]);

  const value = {
    locale,
    messages,
    availableLangs,
    isLoading,
    changeLanguage,
    isKorean: locale === 'ko',
    isEnglish: locale === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
